import {useEffect, useState} from 'react';
import {Form, Input, Select, DatePicker, Collapse} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import {GET_ALL_STOCKS_WITH_INSTRUMENT_KEY} from 'graphql/queries/stocks';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import styles from './geoLocationConfig.module.scss';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import moment from 'moment';
import {RegionRules} from './RegionRules';
import {
  aclOptionsFor,
  entryPrecedence,
  PRODUCT_OPTIONS,
} from './regionAclOptions';

const {Option} = Select;
const {Panel} = Collapse;
const {TextArea} = Input;

const LANGUAGES = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

const getDefaultRegionConfig = () => ({
  region: [],
  cutoffDate: null,
  acl: [],
  rules: [],
  message: {
    firstTimeUser: {
      EN: {title: '', language: 'EN', description: ''},
      FR: {title: '', language: 'FR', description: ''},
    },
    existingUsers: {
      EN: {title: '', language: 'EN', description: ''},
      FR: {title: '', language: 'FR', description: ''},
    },
  },
});

export const GeoLocationConfig = ({data, refetch}) => {
  const [form] = Form.useForm();
  const [openFinishModal, setOpenFinishModal] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState([]);

  const {data: countriesData, loading: loadingCountries} = useQuery(
    GET_ALL_COUNTRIES,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: exchangesData, loading: loadingExchanges} = useQuery(
    GET_ALL_STOCKS_WITH_INSTRUMENT_KEY,
    {errorPolicy: 'all'},
  );

  // Watched rather than read once: which entry is a country's first depends on
  // the order and contents of every entry above it, and both change as the
  // admin edits.
  const watchedEntries = Form.useWatch('regionAcl', form);
  const precedence = entryPrecedence(watchedEntries || []);

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);

  const [updateAppConfig, {loading: loadingUpdate}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const formattedData = data.map(config => ({
        region: config.region || [],
        products: config.products || [],
        cutoffDate: config.cutoffDate ? moment(config.cutoffDate) : null,
        acl: config.acl || [],
        rules: (config.rules || []).map(rule => ({
          assetClassKeys: rule.assetClassKeys || [],
          instrumentKeys: rule.instrumentKeys || [],
          operation: rule.operation || 'ALL',
          exchanges: rule.exchanges || [],
        })),
        message: {
          firstTimeUser: {
            EN: config.message?.firstTimeUser?.find(
              m => m.language === 'EN',
            ) || {
              title: '',
              language: 'EN',
              description: '',
            },
            FR: config.message?.firstTimeUser?.find(
              m => m.language === 'FR',
            ) || {
              title: '',
              language: 'FR',
              description: '',
            },
          },
          existingUsers: {
            EN: config.message?.existingUsers?.find(
              m => m.language === 'EN',
            ) || {
              title: '',
              language: 'EN',
              description: '',
            },
            FR: config.message?.existingUsers?.find(
              m => m.language === 'FR',
            ) || {
              title: '',
              language: 'FR',
              description: '',
            },
          },
        },
      }));
      form.setFieldsValue({regionAcl: formattedData});
      setExpandedPanels(formattedData.map((_, index) => `panel-${index}`));
    } else {
      form.setFieldsValue({regionAcl: []});
    }
  }, [data, form]);

  const handleTranslate = async (fieldName, messageType, targetLang) => {
    const sourceLang = targetLang === 'FR' ? 'EN' : 'FR';
    const sourceTitle = form.getFieldValue([
      'regionAcl',
      fieldName,
      'message',
      messageType,
      sourceLang,
      'title',
    ]);
    const sourceDescription = form.getFieldValue([
      'regionAcl',
      fieldName,
      'message',
      messageType,
      sourceLang,
      'description',
    ]);
    const sourceText = `${sourceTitle || ''}\n${sourceDescription || ''}`;

    if (!sourceText.trim()) {
      toast.error('Please enter text in the source language first');
      return;
    }

    try {
      const {data: translateData} = await translateText({
        variables: {
          sourceLang,
          targetLang,
          sourceText,
        },
      });

      if (
        translateData?.translateText?.statusCode === 400 ||
        translateData?.translateText?.__typename === 'Error'
      ) {
        toast.error(
          'Error Translating Text',
          translateData?.translateText?.message,
        );
      } else {
        const translatedText = translateData?.translateText?.targetText || '';
        const lines = translatedText.split('\n');
        const translatedTitle = lines[0] || '';
        const translatedDescription = lines.slice(1).join('\n') || '';

        form.setFieldsValue({
          regionAcl: [...form.getFieldValue('regionAcl')].map((item, index) => {
            if (index === fieldName) {
              return {
                ...item,
                message: {
                  ...item.message,
                  [messageType]: {
                    ...item.message[messageType],
                    [targetLang]: {
                      ...item.message[messageType][targetLang],
                      title: translatedTitle,
                      description: translatedDescription,
                    },
                  },
                },
              };
            }
            return item;
          }),
        });
        toast.success('Translated Successfully');
      }
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  const handleSubmit = () => {
    const formValues = form.getFieldsValue();
    const precedence = entryPrecedence(formValues.regionAcl || []);
    const formattedData = (formValues.regionAcl || []).map((config, index) => {
      // The gate reads `acl`, `cutoffDate` and `message` from the first entry
      // matching a country and ignores every later one, so an entry whose
      // countries are all claimed already sends none of them. Storing values
      // the server will never read is how config drifts away from behaviour.
      const isSecondary = precedence[index]?.isSecondary;
      const rules = (config.rules || []).map(rule => ({
        assetClassKeys: rule?.assetClassKeys || [],
        instrumentKeys: rule?.instrumentKeys || [],
        operation: rule?.operation || 'ALL',
        exchanges: rule?.exchanges || [],
      }));

      return {
        region: config.region || [],
        // Not gated on isSecondary: this scopes the entry as a whole, rules
        // included, and secondary entries still contribute their rules.
        products: config.products || [],
        cutoffDate:
          !isSecondary && config.cutoffDate
            ? moment(config.cutoffDate).toISOString()
            : null,
        acl: isSecondary ? [] : config.acl || [],
        rules,
        message: {
          firstTimeUser: isSecondary
            ? []
            : [
                config.message?.firstTimeUser?.EN,
                config.message?.firstTimeUser?.FR,
              ]
                .filter(m => m?.title || m?.description)
                .map(({title, language, description}) => ({
                  title,
                  language,
                  description,
                })),
          existingUsers: isSecondary
            ? []
            : [
                config.message?.existingUsers?.EN,
                config.message?.existingUsers?.FR,
              ]
                .filter(m => m?.title || m?.description)
                .map(({title, language, description}) => ({
                  title,
                  language,
                  description,
                })),
        },
      };
    });

    updateAppConfig({
      variables: {
        data: {
          regionAcl: formattedData,
        },
      },
    })
      .then(({data: {updateAppConfig}}) => {
        if (updateAppConfig.__typename === 'Error') {
          toast.error(updateAppConfig.message);
        } else {
          toast.success('Updated Successfully');
          setOpenFinishModal(false);
          if (refetch) {
            refetch();
          }
        }
      })
      .catch(error => toast.error(error.message || 'Update failed'));
  };

  const countries = countriesData?.getAllCountries || [];
  const exchanges = (exchangesData?.getAllStocksExchange || []).filter(
    exchange => exchange?.code,
  );

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>Geo Location Configuration</h2>
        <p>
          Configure region-based access control lists (ACL) and messages for
          users based on their geographic location.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={changedValues => {
          if (changedValues.regionAcl) {
            changedValues.regionAcl.forEach((item, index) => {
              if (
                item &&
                item.acl &&
                item.acl.includes('ALL') &&
                item.acl.length > 1
              ) {
                form.setFields([
                  {name: ['regionAcl', index, 'acl'], value: ['ALL']},
                ]);
              }
            });
          }
        }}
        onFinish={() => setOpenFinishModal(true)}>
        <Form.List name="regionAcl">
          {(fields, {add, remove}) => (
            <>
              {fields.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>
                    No region configurations found. Click &quot;Add Region&quot;
                    to create one.
                  </p>
                </div>
              ) : (
                <Collapse
                  activeKey={expandedPanels}
                  onChange={keys => setExpandedPanels(keys)}
                  className={styles.collapse}>
                  {fields.map(({name: fieldName}) => {
                    const regionValue = form.getFieldValue([
                      'regionAcl',
                      fieldName,
                      'region',
                    ]);
                    const {isSecondary, shadowedCountries = []} =
                      precedence[fieldName] || {};
                    return (
                      <Panel
                        header={
                          <div className={styles.panelHeader}>
                            <span>
                              Region {fieldName + 1}:{' '}
                              {regionValue && regionValue.length > 0
                                ? regionValue.join(', ')
                                : 'No countries selected'}
                            </span>
                            <img
                              onClick={e => {
                                e.stopPropagation();
                                remove(fieldName);
                                setExpandedPanels(
                                  expandedPanels.filter(
                                    panel => panel !== `panel-${fieldName}`,
                                  ),
                                );
                              }}
                              className={styles.deleteButton}
                              src={deleteIcon}
                              alt="Delete"
                            />
                          </div>
                        }
                        key={`panel-${fieldName}`}>
                        <div className={styles.configForm}>
                          <div className={styles.basicFields}>
                            <Form.Item
                              label="Select Countries"
                              className={styles.fullWidth}
                              name={[fieldName, 'region']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select at least one country',
                                },
                              ]}>
                              <Select
                                mode="multiple"
                                placeholder="Select countries"
                                loading={loadingCountries}
                                showSearch
                                filterOption={true}
                                optionFilterProp="filter">
                                {countries.map(country => (
                                  <Option
                                    key={country.countryCode}
                                    value={country.countryCode}
                                    filter={country.name}>
                                    {country.name} ({country.countryCode})
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item
                              label="Apply To Products"
                              className={styles.fullWidth}
                              name={[fieldName, 'products']}
                              extra="Leave empty to restrict both apps. Pick one to restrict only that app — e.g. Mobile only, to lift the restriction on web while keeping it in the mobile app.">
                              <Select
                                mode="multiple"
                                allowClear
                                placeholder="All products">
                                {PRODUCT_OPTIONS.map(option => (
                                  <Option
                                    key={option.value}
                                    value={option.value}>
                                    {option.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>

                            {isSecondary ? (
                              <div className={styles.secondaryNotice}>
                                <h4>Additional entry for the same countries</h4>
                                <p>
                                  Every country here is already covered by an
                                  entry above. Instrument restrictions from all
                                  matching entries are combined, but the access
                                  control list, cutoff date and message are read
                                  from the first entry only — so they are not
                                  editable here. Add them to the first entry for{' '}
                                  {shadowedCountries.join(', ')}.
                                </p>
                              </div>
                            ) : (
                              <>
                                {shadowedCountries.length > 0 && (
                                  <div className={styles.shadowWarning}>
                                    {shadowedCountries.join(', ')} already
                                    appear in an earlier entry. This
                                    entry&apos;s restrictions still apply to
                                    them, but its access control list, cutoff
                                    date and message will be ignored for those
                                    countries.
                                  </div>
                                )}
                                <div className={styles.rowFields}>
                                  <Form.Item
                                    label="Cutoff Date"
                                    className={styles.halfWidth}
                                    name={[fieldName, 'cutoffDate']}
                                    tooltip="Date threshold to distinguish new vs existing users for message and restriction purposes."
                                    rules={[
                                      {
                                        required: !isSecondary,
                                        message: 'Cutoff Date is required',
                                      },
                                    ]}>
                                    <DatePicker
                                      showTime
                                      format="YYYY-MM-DD HH:mm:ss"
                                      style={{width: '100%'}}
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(
                                      prevValues,
                                      currentValues,
                                    ) => {
                                      const prev =
                                        prevValues.regionAcl?.[fieldName];
                                      const curr =
                                        currentValues.regionAcl?.[fieldName];
                                      return (
                                        prev?.acl !== curr?.acl ||
                                        prev?.rules !== curr?.rules
                                      );
                                    }}>
                                    {({getFieldValue}) => {
                                      const currentAcl =
                                        getFieldValue([
                                          'regionAcl',
                                          fieldName,
                                          'acl',
                                        ]) || [];
                                      const isAllSelected =
                                        currentAcl.includes('ALL');
                                      // An entry may restrict purely through
                                      // rules — the guide's own example ships
                                      // an empty acl beside a venue-scoped
                                      // rule — so an action is only required
                                      // when the entry restricts nothing else.
                                      const hasRules = Boolean(
                                        getFieldValue([
                                          'regionAcl',
                                          fieldName,
                                          'rules',
                                        ])?.length,
                                      );

                                      return (
                                        <Form.Item
                                          label="Access Control List (ACL)"
                                          className={styles.halfWidth}
                                          name={[fieldName, 'acl']}
                                          rules={[
                                            {
                                              required: !hasRules,
                                              message:
                                                'Select an action, or add an instrument restriction below',
                                            },
                                          ]}>
                                          <Select
                                            mode="multiple"
                                            placeholder="Select ACL options">
                                            {/* Instrument actions the entry
                                                already stores are listed too,
                                                so they read as their name and
                                                stay removable — narrowing a
                                                restriction to one venue means
                                                taking the broad action off
                                                this list. */}
                                            {aclOptionsFor(currentAcl).map(
                                              option => (
                                                <Option
                                                  key={option.value}
                                                  value={option.value}
                                                  disabled={
                                                    isAllSelected &&
                                                    option.value !== 'ALL'
                                                  }>
                                                  {option.label}
                                                </Option>
                                              ),
                                            )}
                                          </Select>
                                        </Form.Item>
                                      );
                                    }}
                                  </Form.Item>
                                </div>
                              </>
                            )}
                          </div>

                          <RegionRules
                            form={form}
                            fieldName={fieldName}
                            exchanges={exchanges}
                            loadingExchanges={loadingExchanges}
                          />

                          {!isSecondary && (
                            <div className={styles.messagesContainer}>
                              <div className={styles.messageSection}>
                                <h3>First Time User Message</h3>
                                <div className={styles.languagesRow}>
                                  {LANGUAGES.map(lang => (
                                    <div
                                      key={lang.value}
                                      className={styles.languageSection}>
                                      <div className={styles.languageHeader}>
                                        <h4>{lang.label}</h4>
                                        {lang.value === 'EN' && (
                                          <Button
                                            type="secondary"
                                            label="Translate"
                                            disabled={loadingTranslation}
                                            onClick={() =>
                                              handleTranslate(
                                                fieldName,
                                                'firstTimeUser',
                                                'FR',
                                              )
                                            }
                                          />
                                        )}
                                      </div>
                                      <Form.Item
                                        label="Title"
                                        name={[
                                          fieldName,
                                          'message',
                                          'firstTimeUser',
                                          lang.value,
                                          'title',
                                        ]}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Title is required',
                                          },
                                        ]}>
                                        <Input
                                          placeholder={`Enter title in ${lang.label}`}
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        name={[
                                          fieldName,
                                          'message',
                                          'firstTimeUser',
                                          lang.value,
                                          'language',
                                        ]}
                                        hidden>
                                        <Input value={lang.value} />
                                      </Form.Item>
                                      <Form.Item
                                        label="Description"
                                        name={[
                                          fieldName,
                                          'message',
                                          'firstTimeUser',
                                          lang.value,
                                          'description',
                                        ]}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Description is required',
                                          },
                                        ]}>
                                        <TextArea
                                          rows={4}
                                          placeholder={`Enter description in ${lang.label}`}
                                        />
                                      </Form.Item>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className={styles.messageSection}>
                                <h3>Existing Users Message</h3>
                                <div className={styles.languagesRow}>
                                  {LANGUAGES.map(lang => (
                                    <div
                                      key={lang.value}
                                      className={styles.languageSection}>
                                      <div className={styles.languageHeader}>
                                        <h4>{lang.label}</h4>
                                        {lang.value === 'EN' && (
                                          <Button
                                            type="secondary"
                                            label="Translate"
                                            disabled={loadingTranslation}
                                            onClick={() =>
                                              handleTranslate(
                                                fieldName,
                                                'existingUsers',
                                                'FR',
                                              )
                                            }
                                          />
                                        )}
                                      </div>
                                      <Form.Item
                                        label="Title"
                                        name={[
                                          fieldName,
                                          'message',
                                          'existingUsers',
                                          lang.value,
                                          'title',
                                        ]}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Title is required',
                                          },
                                        ]}>
                                        <Input
                                          placeholder={`Enter title in ${lang.label}`}
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        name={[
                                          fieldName,
                                          'message',
                                          'existingUsers',
                                          lang.value,
                                          'language',
                                        ]}
                                        hidden>
                                        <Input value={lang.value} />
                                      </Form.Item>
                                      <Form.Item
                                        label="Description"
                                        name={[
                                          fieldName,
                                          'message',
                                          'existingUsers',
                                          lang.value,
                                          'description',
                                        ]}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Description is required',
                                          },
                                        ]}>
                                        <TextArea
                                          rows={4}
                                          placeholder={`Enter description in ${lang.label}`}
                                        />
                                      </Form.Item>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </Panel>
                    );
                  })}
                </Collapse>
              )}

              <div
                className={styles.addButton}
                onClick={() => {
                  add(getDefaultRegionConfig());
                  const newIndex = fields.length;
                  setExpandedPanels([...expandedPanels, `panel-${newIndex}`]);
                }}>
                <img src={plusIcon} alt="Add" />
                <h1>Add Region Configuration</h1>
              </div>
            </>
          )}
        </Form.List>

        <div className={styles.submitContainer}>
          <SubmitButton label="Update" />
        </div>
      </Form>

      <ConfirmModal
        openModal={openFinishModal}
        setOpenModal={() => setOpenFinishModal(false)}
        confirmText="Are you sure you want to update the geo location configuration?"
        title="Confirm Update"
        loading={loadingUpdate}
        handleOperation={handleSubmit}
      />
    </div>
  );
};
